// components/maintenance/DetailDocsDialog.jsx
import React, { useState, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
    Tooltip, IconButton, Typography, DialogContentText
} from '@mui/material';
import { Delete as DeleteIcon, Visibility as VisibilityIcon, Download as DownloadIcon, Edit as EditIcon } from "@mui/icons-material";
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid';
import MaintenanceDocForm from './MaintenanceDocForm';
import { useMaintenanceDocs } from '../../../hooks/maintenance/useMaintenanceDocs';
import { useAuthContext } from '../../../context/AuthProvider';

function Toolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarDensitySelector />
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

export default function MaintenanceDocsDialog({ open, onClose, manId, row, apiBase }) {
    const { user } = useAuthContext();
    const {
        maintenanceDocs,
        isLoading: isLoadingDocs,
        error: errorDocs,
        createMutation: createDocMutation,
        updateMutation: updateDocMutation,
        deleteMutation: deleteDocMutation, 
    } = useMaintenanceDocs(parseInt(manId, 10));

    const [formOpen, setFormOpen] = useState(false);
    const [docFormState, setDocFormState] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [openPreview, setOpenPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [confirmDeleteDocDialog, setConfirmDeleteDocDialog] = useState({ open: false, doc: null });

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleOpenDocument = (docRuta) => {
        if (!docRuta) {
            showAlert("Ruta del documento no disponible para previsualizar.", "error");
            return;
        }

        const fullUrl = `${process.env.REACT_APP_API_URL}/documents/file/${docRuta}`;        

        setPreviewUrl(fullUrl);
        setOpenPreview(true);
    };

    const handleClosePreview = () => {
        setOpenPreview(false);
        setPreviewUrl(null);
    };

    const handleOpenDocForm = (doc = null) => {
        setFormOpen(true);
        setDocFormState(doc);
    };

    const handleCloseDocForm = () => {
        setFormOpen(false);
        setDocFormState(null);
    };

    const handleSubmitDoc = useCallback((formDataFromChild) => {

        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión para realizar esta acción.", "error");
            return;
        }

        const { docnom, tipdoc, tipcomid, selectedFile } = formDataFromChild;

        const formDataToSendToAPI = new FormData();

        // Añadir campos de texto al FormData
        formDataToSendToAPI.append("manid", manId);
        formDataToSendToAPI.append("docnom", docnom);
        formDataToSendToAPI.append("tipdoc", tipdoc);
        if (tipcomid) {
            formDataToSendToAPI.append("tipcomid", tipcomid);
        } else if (tipdoc === 'CONTABLE') {
            formDataToSendToAPI.append("tipcomid", '');
        }

        // Manejo del archivo
        if (selectedFile) { // Usa el archivo que viene del formulario hijo
            formDataToSendToAPI.append("document", selectedFile);
        }

        if (docFormState) {
            formDataToSendToAPI.append("updbyid", user.EmpID);
            updateDocMutation.mutate(
                { id: docFormState.docid, formData: formDataToSendToAPI }, // Pasa el FormData aquí
                {
                    onSuccess: () => {
                        showAlert("Documento actualizado correctamente");
                        handleCloseDocForm();
                    },
                    onError: (mutationError) => {
                        console.error("Error al actualizar documento:", mutationError);
                        showAlert(mutationError.message || "Error al actualizar", "error");
                    },
                }
            );
        } else { 
            if (!selectedFile) {
                showAlert("Debe seleccionar un archivo para subir.", "error");
                return;
            }
            formDataToSendToAPI.append("crebyid", user.EmpID);
            createDocMutation.mutate(formDataToSendToAPI, {
                onSuccess: () => {
                    showAlert("Documento subido correctamente");
                    handleCloseDocForm();
                },
                onError: (mutationError) => {
                    console.error("Error al subir documento:", mutationError);
                    showAlert(mutationError.message || "Error al subir", "error");
                },
            });
        }
    }, [docFormState, createDocMutation, updateDocMutation, user, manId]);

    const handleConfirmDeleteDoc = () => {
        if (!user || !user.EmpID) {
            showAlert("Debe iniciar sesión.", "error");
            setConfirmDeleteDocDialog({ open: false, doc: null });
            return;
        }
        deleteDocMutation.mutate(
            { id: confirmDeleteDocDialog.doc.docid, updbyid: user.EmpID },
            {
                onSuccess: () => {
                    showAlert("Documento eliminado correctamente");
                    setConfirmDeleteDocDialog({ open: false, doc: null });
                },
                onError: (mutationError) => {
                    console.error("Error al eliminar documento:", mutationError);
                    showAlert(mutationError.message || "Error al eliminar", "error");
                },
            }
        );
    };

    const columns = [
        //{ field: 'docid', headerName: 'ID', width: 80 },
        {
            field: 'rowNumber',
            headerName: 'Item',
            width: 80,
            sortable: false,
            filterable: false,
            valueGetter: (params) =>
                params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
        },
        { field: 'docnom', headerName: 'Nombre', flex: 1 },
        { field: 'tipdoc', headerName: 'Tipo', width: 200 },
        {
            field: 'credat', headerName: 'Creado', width: 200,
            valueGetter: (p) => new Date(p.row.credat).toLocaleString()
        },
        {
            field: 'acciones', headerName: 'Acciones', width: 150, sortable: false,
            renderCell: (p) => (

                <Box display="flex" gap={1}>
                    <Tooltip title="Ver Documento"> {/* Tooltip para ver */}
                        <IconButton
                            edge="end"
                            aria-label="view"
                            onClick={() => handleOpenDocument(p.row.docruta)} // Llama a la nueva función para previsualizar
                            color="info" // Puedes mantener el color que tenías o cambiarlo
                        >
                            <VisibilityIcon /> {/* Icono de visibilidad */}
                        </IconButton>
                    </Tooltip>
                    {['CREADO', 'INICIADO', 'EN_PROCESO'].includes(row.manest) && (
                        <Tooltip title="Editar Documento">
                            <IconButton edge="end" aria-label="edit" onClick={() => handleOpenDocForm(p.row)}>
                                <EditIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {['CREADO', 'INICIADO', 'EN_PROCESO'].includes(row.manest) && (
                        <Tooltip title="Eliminar Documento">
                            {/*<IconButton edge="end" aria-label="delete" onClick={() => deleteDocMutation.mutate(p.row.docid)}>*/}
                            <IconButton edge="end" aria-label="delete" onClick={() => setConfirmDeleteDocDialog({ open:true, doc: p.row })}>
                                <DeleteIcon color="error" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )
        }
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Documentos — Mantenimiento #{manId}</DialogTitle>
            <DialogContent dividers>
                <Box height={360}>
                    <DataGrid
                        rows={maintenanceDocs}
                        columns={columns}
                        loading={isLoadingDocs}
                        getRowId={(r) => r.docid}
                        disableRowSelectionOnClick
                        slots={{ toolbar: Toolbar }}
                    />
                </Box>

                <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button variant="contained" onClick={() => handleOpenDocForm()}>
                        Añadir documento
                    </Button>
                </Box>

                {formOpen && (
                    <MaintenanceDocForm
                        open={formOpen}
                        handleClose={handleCloseDocForm}
                        defaultValues={{
                            manid: manId,
                            mandetid: manId,
                            mantenimientonombre: `Mantenimiento ${manId}`,
                            ...(docFormState ? {
                                // prefill en edición
                                docid: docFormState.docid,
                                docnom: docFormState.docnom,
                                tipdoc: docFormState.tipdoc,
                                tipcomid: docFormState.tipcomid ?? '',
                                docruta: docFormState.docruta ?? '',
                            } : {})
                        }}
                        onSubmit={handleSubmitDoc}
                    />
                )}

                {/* Modal para visualizar el documento */}
                <Dialog open={openPreview} onClose={handleClosePreview} fullWidth maxWidth="lg">
                    <DialogTitle>Vista Previa del Documento</DialogTitle>
                    <DialogContent dividers sx={{ p: 0 }}>
                        {previewUrl ? (
                            previewUrl.toLowerCase().endsWith(".pdf") ? (
                                <iframe
                                    src={previewUrl}
                                    width="100%"
                                    height="500px" // Altura fija para el iframe
                                    style={{ border: "none" }}
                                    title="Vista previa del documento"
                                />
                            ) : previewUrl.toLowerCase().match(/\.(jpeg|jpg|gif|png|webp)$/) ? ( // Regex para imágenes
                                <img
                                    src={previewUrl}
                                    alt="Documento adjunto"
                                    style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }}
                                />
                            ) : (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body1">Tipo de archivo no soportado para previsualización.</Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => window.open(previewUrl, '_blank')}
                                        startIcon={<DownloadIcon />}
                                        sx={{ mt: 2 }}
                                    >
                                        Descargar Archivo
                                    </Button>
                                </Box>
                            )
                        ) : (
                            <Typography variant="body1" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                                No hay documento disponible para previsualizar.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClosePreview} color="secondary">
                            Cerrar
                        </Button>
                    </DialogActions>
                </Dialog>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
            <Dialog open={confirmDeleteDocDialog.open} onClose={() => setConfirmDeleteDocDialog({ open: false, doc: null })}>
                <DialogTitle>Confirmar eliminación de documento</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el documento "{confirmDeleteDocDialog.doc?.docnom}"?
                        Esta acción es irreversible y eliminará el archivo asociado.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDocDialog({ open: false, doc: null })} color="inherit">Cancelar</Button>
                    <Button onClick={handleConfirmDeleteDoc} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
